// User create/edit dialog
import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle, X } from 'lucide-react'
import { useUsers } from '@/hooks/useUsers'
import { useCustomers } from '@/hooks/useCustomers'
import type { User, UserRole, CreateUserRequest, UpdateUserRequest } from '@/types/user'
import { USER_ROLES } from '@/types/user'

interface UserDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  user: User | null
}

export function UserDialog({ open, onOpenChange, user }: UserDialogProps) {
  const { createUser, updateUser } = useUsers()
  const { customers } = useCustomers()

  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState<UserRole>('viewer')
  const [hasAllCustomerAccess, setHasAllCustomerAccess] = useState(false)
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([])
  const [isActive, setIsActive] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (user) {
      setEmail(user.email)
      setFullName(user.full_name)
      setRole(user.role)
      setHasAllCustomerAccess(user.has_all_customer_access)
      setIsActive(user.is_active)
      setPassword('') // Don't show password for existing users
    } else {
      setEmail('')
      setFullName('')
      setPassword('')
      setRole('viewer')
      setHasAllCustomerAccess(false)
      setSelectedCustomers([])
      setIsActive(true)
    }
    setError(null)
  }, [user, open])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)

    try {
      if (user) {
        // Update existing user
        const updates: UpdateUserRequest = {
          user_id: user.id,
          full_name: fullName,
          role,
          has_all_customer_access: hasAllCustomerAccess,
          is_active: isActive,
        }
        await updateUser(updates)
      } else {
        // Create new user
        if (!password) {
          setError('Password is required for new users')
          setSaving(false)
          return
        }

        const newUser: CreateUserRequest = {
          email,
          password,
          full_name: fullName,
          role,
          has_all_customer_access: hasAllCustomerAccess,
          customer_ids: hasAllCustomerAccess ? [] : selectedCustomers,
        }
        await createUser(newUser)
      }
      onOpenChange(false)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setSaving(false)
    }
  }

  const toggleCustomer = (customerId: string) => {
    setSelectedCustomers(prev =>
      prev.includes(customerId)
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    )
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => onOpenChange(false)}>
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">{user ? 'Edit' : 'Create'} User</h2>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email */}
          <div>
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={!!user} // Can't change email for existing users
              required
            />
          </div>

          {/* Full Name */}
          <div>
            <Label htmlFor="fullName">Full Name</Label>
            <Input
              id="fullName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="e.g., John Doe"
            />
          </div>

          {/* Password (only for new users) */}
          {!user && (
            <div>
              <Label htmlFor="password">Password *</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                placeholder="Minimum 8 characters"
              />
            </div>
          )}

          {/* Role */}
          <div>
            <Label htmlFor="role">Role *</Label>
            <select
              id="role"
              value={role}
              onChange={(e) => setRole(e.target.value as UserRole)}
              className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            >
              {USER_ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label} - {r.description}
                </option>
              ))}
            </select>
          </div>

          {/* All Customer Access */}
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="allCustomerAccess">Access All Customers</Label>
              <p className="text-sm text-muted-foreground">
                User can access all customers in the organization
              </p>
            </div>
            <input
              id="allCustomerAccess"
              type="checkbox"
              checked={hasAllCustomerAccess}
              onChange={(e) => setHasAllCustomerAccess(e.target.checked)}
              className="h-5 w-5"
            />
          </div>

          {/* Customer Access Selection */}
          {!hasAllCustomerAccess && customers.length > 0 && (
            <div className="rounded-lg border p-4">
              <Label className="mb-2 block">Customer Access</Label>
              <p className="text-sm text-muted-foreground mb-3">
                Select which customers this user can access
              </p>
              <div className="max-h-48 space-y-2 overflow-y-auto">
                {customers.map((customer) => (
                  <label
                    key={customer.id}
                    className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={selectedCustomers.includes(customer.id)}
                      onChange={() => toggleCustomer(customer.id)}
                      className="h-4 w-4"
                    />
                    <span className="text-sm">{customer.name}</span>
                    {customer.company_code && (
                      <span className="text-xs text-gray-500">({customer.company_code})</span>
                    )}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Active Status */}
          {user && (
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label htmlFor="isActive">Active</Label>
                <p className="text-sm text-muted-foreground">
                  Inactive users cannot log in
                </p>
              </div>
              <input
                id="isActive"
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="h-5 w-5"
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2"
            >
              {saving ? 'Saving...' : user ? 'Update User' : 'Create User'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
